import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { FaCheck } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { Error } from '@material-ui/icons';
import {
  FlightFareSources, FlightSearchTypes, SearchModes, SearchStatus,
} from '../../../../../constants';
import withSpinner from '../../../../../HOCs/withSpinner';
import AddButton from '../../../../AddButton';
import Button from '../../../../Button';
import OneWayFlightInfo from './OneWayFlightInfo';
import Label from '../../../../Label';
import AgentCommission from '../../../AgentCommission';
import currencySymbol from '../../../../../helpers/currencySymbol';
import RemoveFromCompare from '../../../RemoveFromCompare';
import TripBookButton from '../../../../TripConfiguration/ViewTripConfigurationItems/TripBookButton';
import checkIsNdcFare from '../../../../../helpers/checkNdcFare';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { checkDoEnableSelectFlightsBySegment } from '../../../../../store/slices/user';
import checkIsFlightTripSearch from '../../../../../helpers/selectors/checkIsFlightTripSearch';

const FlightInfo = ({
  itinerary, tripId, doShowPrice, doCheckoutOnSelect, onCheckout, selected,
  onAddToTripClick, onRemoveFromTrip, isConfirmation, showHideFlightDetailElement,
  isViewingBrochure, updateBrochurePriceStatus,
}) => {
  const router = useRouter();
  const { isViewingCompare } = useSelector((state) => state.compare);
  const { isLoggedIn } = useSelector((state) => state.user);
  const { selectedSearchMode } = useSelector((state) => state.tripSearch);
  const isUpdatingBrochurePrice = isViewingBrochure
    && updateBrochurePriceStatus === SearchStatus.SEARCHING;

  const isNdcFare = checkIsNdcFare(itinerary);
  const brochure = useSelector((state) => state.brochure.brochures[tripId]);
  const doAllowBook = brochure && brochure.doAllowBook;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const doShowBookButton = (doAllowBook || isLoggedIn) && isBrochurePage
    && !itinerary.isUnavailableRate && !isUpdatingBrochurePrice;
  const doRenderBookButton = !!doShowBookButton;
  const { validateFlightStatus } = useSelector((store) => store.flight);
  const validateFlightStatusForItinerary = validateFlightStatus
   && validateFlightStatus[itinerary.flightKey] && validateFlightStatus[itinerary.flightKey].status;
  const validateFlightError = validateFlightStatus
   && validateFlightStatus[itinerary.flightKey] && validateFlightStatus[itinerary.flightKey].error;
  const isTripSearch = selectedSearchMode === SearchModes.FLIGHT;
  const isFlightTripSearch = useSelector(checkIsFlightTripSearch(isTripSearch));
  const {
    searchObj, breadCrumbsIndex,
  } = useSelector(flightSearchInput(tripId, isFlightTripSearch));
  const { flightSegments = [] } = searchObj || {};
  let doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  if (isViewingBrochure || isViewingCompare || (!isTripSearch && selected) || isConfirmation
  || searchObj.flightSearchType === FlightSearchTypes.DIRECT_SELL
  || searchObj.flightSearchType === FlightSearchTypes.ONE_WAY) {
    doEnableSelectFlightsBySegment = false;
  }

  const saveTripStatus = useSelector(
    (store) => ((store.trip.selectedTrips[tripId] || {}).saveTripStatus),
  );
  return (
    <div className="grid grid-cols-10 email-display-flex">
      <div className="grid grid-rows-2 col-span-7 md:col-span-8 text-sm md:text-base gap-2">
        {itinerary.flightSegments && itinerary.flightSegments.map((flightSegment,
          index) => (!doEnableSelectFlightsBySegment || itinerary.flightSegments.length === 1
            || breadCrumbsIndex === index ? (
              <OneWayFlightInfo
                flightSegment={flightSegment}
                key={`${index.toString()}`}
              />
          ) : null))}
      </div>

      <div className="md:flex flex-row-reverse md:flex-col justify-between text-center col-span-3 md:col-span-2 email-normal-line-height email-ml-20 text-sm md:text-base self-center md:self-stretch">
        {doShowPrice && (
          <div>
            <div className="font-bold">
              {itinerary
                && itinerary.cost && withSpinner(() => (!itinerary.isUnavailableRate
                ? (
                  <>
                    <NumberFormat value={itinerary.cost.price} displayType="text" thousandSeparator prefix={currencySymbol(itinerary.cost.currencyCode)} fixedDecimalScale="true" decimalScale="2" />
                    <p className="font-normal">
                      for all travelers
                    </p>
                  </>
                )
                : <span className="italic">Flight unavailable</span>
              ), isUpdatingBrochurePrice, 'Retrieving updated price', 'inline', 'xs')}
            </div>

            {tripId && !isViewingBrochure && (
              <div>
                {doCheckoutOnSelect ? (
                  <Button
                    size={Button.buttonSizes.SM}
                    onClick={onCheckout}
                  >
                    Select
                  </Button>
                ) : (
                  <AddButton
                    isSelected={selected}
                    onAdd={onAddToTripClick}
                    onRemove={onRemoveFromTrip}
                    addLabel={doEnableSelectFlightsBySegment && breadCrumbsIndex < flightSegments.length - 1 ? 'Select' : 'Add to Trip'}
                    removeLabel="Remove"
                    disabled={validateFlightStatusForItinerary === SearchStatus.SEARCHING
                      || (validateFlightStatusForItinerary === SearchStatus.DONE
                         && validateFlightError)}
                    doShowLoadingState={validateFlightStatusForItinerary === SearchStatus.SEARCHING}
                  />
                )}
              </div>
            )}
            {doRenderBookButton && (
              <div className="w-full flex justify-center items-center">
                <div className="w-20">
                  <TripBookButton
                    tripId={tripId}
                    saveTripStatus={saveTripStatus}
                    buttonColorCssClass="bg-button-theme text-button-theme"
                    buttonGroupStyles={{ minWidth: 'unset', paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
                    onBookClick={() => onCheckout()}
                    disabled={isBrochurePage && isNdcFare}
                    disableBookingTooltip="We're sorry, booking NDC fares from the brochure page is not currently supported."
                  />
                </div>
              </div>
            )}
            {Boolean(validateFlightError) && (
              <Label level="danger" className="text-sm text-left mb-2" text={validateFlightError} icon={<Error className="text-current" />} />
            )}
          </div>
        )}
        <div>
          <div className="text-sm">
            {itinerary.cabinFareBrandNames}
            <div className="text-gray-600">
              Cabin:
              {' '}
              {itinerary.cabinClassNames}
            </div>
          </div>
          {doShowPrice && (
          <div className="text-sm">
            <Label
              text={itinerary.flightSegments[0].flights[0]
                .pricingSubsource === FlightFareSources.NDC ? (
                  <>
                    {`${itinerary.refundabilityLabel},`}
                    <b> NDC Fare</b>
                  </>
                ) : _.capitalize(itinerary.refundabilityLabel)}
              level=""
              descriptionOnHover={itinerary.partialRefundPolicyText}
              tooltipPlacement="top"
            />
          </div>
          )}
          {itinerary.flightSegments[0].flights[0].fareAcccountCode
            ? (
              <div className="text-sm justify-center flex">
                {'  '}
                <Label
                  text={`${itinerary.flightSegments[0].flights[0].fareAcccountCode} rate code match`}
                  icon={<FaCheck />}
                  level="success"
                  italic
                  inline
                  descriptionOnHover={`${itinerary.flightSegments[0].flights[0].fareAcccountCode} rate code match with your selection.`}
                />
              </div>
            ) : <></>}
          {itinerary.agentCommission > 0 ? (
            <AgentCommission mode={itinerary.mode} agentCommission={itinerary.agentCommission} currencyCode={itinerary.cost && itinerary.cost.currencyCode} className="py-1 text-xs inline-flex" />
          ) : <></>}
        </div>
        {(isConfirmation || !tripId) && (
          <div className="inline-block text-center">
            {showHideFlightDetailElement}
          </div>
        )}
        {isViewingCompare && (
        <RemoveFromCompare
          itineraryId={itinerary.flightKey}
          itinerary={itinerary}
        />
        )}
      </div>

    </div>
  );
};

FlightInfo.propTypes = {
  itinerary: PropTypes.objectOf(PropTypes.objectOf),
  tripId: PropTypes.string,
  doShowPrice: PropTypes.bool,
  doCheckoutOnSelect: PropTypes.bool,
  onCheckout: PropTypes.func,
  selected: PropTypes.bool,
  onAddToTripClick: PropTypes.func,
  onRemoveFromTrip: PropTypes.func,
  isConfirmation: PropTypes.bool,
  showHideFlightDetailElement: PropTypes.element,
  isViewingBrochure: PropTypes.bool,
  updateBrochurePriceStatus: PropTypes.number,
};

FlightInfo.defaultProps = {
  itinerary: {},
  tripId: '',
  doShowPrice: true,
  doCheckoutOnSelect: false,
  onCheckout: undefined,
  selected: false,
  onAddToTripClick: undefined,
  onRemoveFromTrip: undefined,
  isConfirmation: false,
  showHideFlightDetailElement: undefined,
  isViewingBrochure: false,
  updateBrochurePriceStatus: undefined,
};
export default FlightInfo;
